import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { History, ArrowRight, User as UserIcon } from "lucide-react";
import { fmtDateTime } from "@/utils/format";

/**
 * Renders a compact timeline of changes for a quote or order.
 * Fetches `GET /{entity}/{id}/history` and shows old → new per row.
 */
export default function AuditLogPanel({ entity, entityId, open, refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!open || !entityId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const path = entity === "order" ? `/orders/${entityId}/history` : `/quotes/${entityId}/history`;
        const { data } = await api.get(path);
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [entity, entityId, open, refreshKey]);

  if (!open) return null;

  return (
    <div className="border-2 border-[#09090B] bg-white" data-testid={`${entity}-audit-panel`}>
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-2 px-4 py-3 border-b-2 border-[#09090B] bg-neutral-50 hover:bg-neutral-100"
        data-testid={`${entity}-audit-toggle`}
      >
        <History size={16} />
        <span className="text-[11px] font-mono uppercase tracking-widest">Değişiklik Geçmişi</span>
        <span className="ml-auto text-[10px] font-mono text-neutral-500" data-testid={`${entity}-audit-count`}>
          {items ? `${items.length} kayıt` : loading ? "yükleniyor…" : ""}
        </span>
        <span className="text-xs font-mono text-neutral-500">{collapsed ? "▸" : "▾"}</span>
      </button>

      {!collapsed && (
        <div className="max-h-80 overflow-y-auto">
          {loading && (
            <div className="p-6 text-center text-xs font-mono text-neutral-500">Yükleniyor…</div>
          )}
          {!loading && items && items.length === 0 && (
            <div className="p-6 text-center text-xs font-mono text-neutral-500 italic">Henüz kayıt yok</div>
          )}
          {!loading && items && items.length > 0 && (
            <ul className="divide-y divide-neutral-200">
              {items.map((r) => (
                <li key={r.id} className="px-4 py-2.5 hover:bg-neutral-50" data-testid={`${entity}-audit-row`}>
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase text-neutral-500">
                    <UserIcon size={10} />
                    <span className="truncate max-w-[240px]" title={r.actor_email}>{r.actor_name}</span>
                    <span>·</span>
                    <span>{fmtDateTime(r.at)}</span>
                  </div>
                  <div className="mt-1 text-sm">
                    {r.action === "action" ? (
                      <div className="flex items-center gap-2">
                        <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase border-2 border-[#0055FF] text-[#0055FF] bg-white">
                          {r.label}
                        </span>
                        {r.new && <span className="text-xs text-neutral-600 font-mono">{r.new}</span>}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{r.label}</span>
                        <span className="px-1.5 py-0.5 text-xs font-mono border border-neutral-300 text-neutral-500 line-through decoration-neutral-400">
                          {r.old}
                        </span>
                        <ArrowRight size={12} className="text-neutral-400" />
                        <span className="px-1.5 py-0.5 text-xs font-mono border-2 border-[#09090B] bg-yellow-50">
                          {r.new}
                        </span>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
